import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  ageAt,
  formatAge,
  formatDay,
  parseIsoDay,
  REFERRAL_URGENCY_LABELS,
  SICK_SHEET_RECOMMENDATION_LABELS,
} from '@wodi/domain';
import type {
  PrintedPatientView,
  ReferralPrintView,
  ReferralSummaryView,
  SickSheetPrintView,
} from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  printSheet,
} from '@wodi/ui';
import { useRef, type Ref } from 'react';

import { ApiError } from '../api/client.ts';
import {
  markReferralPrinted,
  markSickSheetPrinted,
  referralPrintQuery,
  sickSheetPrintQuery,
} from '../api/clinical-documents.ts';
import { formatInstant } from '../patients/format.ts';
import styles from './documents.module.css';

// Printed sick sheets (A5) and referral letters (A4) (docs/plans/phase1-consultation.md
// §7.4, §7.6; C19, S4, S6). The words are keyed so a reviewed Kiswahili version can be
// added without touching the templates (DESIGN.md §9; TODO(verify-spec): the translation).
// Every print calls its `…/printed` route, which counts and audits it; a reprint says
// "Copy 2".

export const DOCUMENT_PRINT_TEXT = {
  en: {
    sickSheet: 'Sick sheet',
    referral: 'Referral letter',
    copy: (n: number) => `Copy ${String(n)}`,
    sickSheetNo: 'Sick sheet no.',
    referralNo: 'Referral no.',
    issued: 'Date',
    patient: 'Patient',
    patientNo: 'Patient no.',
    sexAge: 'Sex, age',
    ageNotRecorded: 'age not recorded',
    estimated: 'estimated',
    recommendation: 'Recommendation',
    restFrom: 'From',
    restTo: 'To, inclusive',
    days: (n: number) => (n === 1 ? '1 day' : `${String(n)} days`),
    period: 'Period',
    diagnosis: 'Diagnosis',
    remarks: 'Remarks',
    certify:
      'This is to certify that the patient named above was seen at this facility and is advised as follows.',
    to: 'To',
    mflCode: 'MFL code',
    service: 'Department or clinician',
    urgency: 'Urgency',
    reason: 'Reason for referral',
    diagnoses: 'Diagnoses',
    noDiagnosis: 'None recorded',
    allergies: 'Allergies',
    noKnownAllergies: 'No known allergies',
    allergiesNotRecorded: 'Allergies not recorded',
    vitals: 'Latest vitals',
    vitalsNotTaken: 'Not taken',
    medicines: 'Current medicines',
    noMedicines: 'None prescribed in this visit',
    results: 'Results',
    summary: 'Summary',
    clinician: 'Clinician',
    licenceNotRecorded: 'Licence not recorded',
    signature: 'Signature',
    stamp: 'Facility stamp',
  },
} as const;

const T = DOCUMENT_PRINT_TEXT.en;

function isoDayWords(text: string): string {
  const day = parseIsoDay(text);
  return day.ok ? formatDay(day.value) : text;
}

export function patientLines(patient: PrintedPatientView, at: string) {
  let age: string = T.ageNotRecorded;
  if (patient.birthDate !== null) {
    const day = parseIsoDay(patient.birthDate);
    if (day.ok) {
      age = formatAge(ageAt(day.value, new Date(at)));
      if (patient.birthDateEstimated) age = `${age} (${T.estimated})`;
    }
  }
  return [
    { label: T.patient, value: patient.name },
    { label: T.patientNo, value: patient.patientNumber, identifier: true },
    { label: T.sexAge, value: `${patient.sexDisplay}, ${age}` },
  ];
}

export function title(base: string, copy: number): string {
  return copy > 1 ? `${base}, ${T.copy(copy)}` : base;
}

export function ClinicianFooter({ display, licence }: { display: string; licence: string | null }) {
  return (
    <PrintFooter>
      {`${T.clinician}: ${display}`}
      <br />
      {licence ?? T.licenceNotRecorded}
      <br />
      {`${T.signature}: ____________________`}
      <br />
      {`${T.stamp}:`}
    </PrintFooter>
  );
}

/** The A5 sick sheet: identity, the rest period and the clinician; nothing else clinical. */
export function SickSheetSheet({
  print,
  sheetRef,
}: {
  print: SickSheetPrintView;
  sheetRef?: Ref<HTMLElement>;
}) {
  const s = print.sickSheet;
  return (
    <PrintSheet
      paper="a5"
      label={T.sickSheet}
      {...(sheetRef === undefined ? {} : { ref: sheetRef })}
    >
      <PrintHeader facility={print.facility.name} title={title(T.sickSheet, print.copy)} />
      <PrintLines
        items={[
          { label: T.sickSheetNo, value: s.sickSheetNumber, identifier: true },
          { label: T.issued, value: formatInstant(s.issuedAt) },
          ...patientLines(print.patient, s.issuedAt),
        ]}
      />
      <p className={styles.printText}>{T.certify}</p>
      <PrintLines
        items={[
          {
            label: T.recommendation,
            value: SICK_SHEET_RECOMMENDATION_LABELS[s.recommendation],
          },
          { label: T.restFrom, value: isoDayWords(s.restFrom) },
          { label: T.restTo, value: isoDayWords(s.restTo) },
          { label: T.period, value: T.days(s.days) },
          ...(s.diagnosisShown && s.diagnosisText !== null
            ? [{ label: T.diagnosis, value: s.diagnosisText }]
            : []),
          ...(s.remarks === null ? [] : [{ label: T.remarks, value: s.remarks }]),
        ]}
      />
      <ClinicianFooter display={s.issuerDisplay} licence={s.issuerLicenceDisplay} />
    </PrintSheet>
  );
}

/** The allergy line in its three explicit forms: the list, none known, or not recorded. */
export function summaryAllergies(summary: ReferralSummaryView): string {
  return summary.allergies.state === 'allergies'
    ? summary.allergies.items.join(', ')
    : summary.allergies.state === 'no_known_allergies'
      ? T.noKnownAllergies
      : T.allergiesNotRecorded;
}

/**
 * The snapshotted visit summary as printed: diagnoses, vitals, medicines, the chosen results
 * and the clinician's words under `wordsHeading`. Shared by the referral letter and the
 * feedback letter (docs/plans/phase2-referrals.md §1.1 item 5).
 */
export function SummaryPrint({
  summary,
  wordsHeading,
}: {
  summary: ReferralSummaryView;
  wordsHeading: string;
}) {
  return (
    <>
      <h3 className={styles.printHeading}>{T.diagnoses}</h3>
      <p className={styles.printText}>
        {summary.diagnoses.length === 0
          ? T.noDiagnosis
          : summary.diagnoses
              .map((d) => (d.code === null ? d.display : `${d.display} (${d.code})`))
              .join('; ')}
      </p>
      <h3 className={styles.printHeading}>{T.vitals}</h3>
      <p className={styles.printText}>
        {summary.vitals.length === 0
          ? T.vitalsNotTaken
          : `${summary.vitals.map((v) => `${v.label} ${v.value}`).join(', ')}${
              summary.vitalsTakenAt === null ? '' : ` (${formatInstant(summary.vitalsTakenAt)})`
            }`}
      </p>
      <h3 className={styles.printHeading}>{T.medicines}</h3>
      <p className={styles.printText}>
        {summary.medicines.length === 0
          ? T.noMedicines
          : summary.medicines.map((m) => `${m.display}: ${m.directions}`).join('; ')}
      </p>
      {summary.results.length === 0 ? null : (
        <>
          <h3 className={styles.printHeading}>{T.results}</h3>
          {summary.results.map((res) => (
            <p key={res.id} className={styles.printText}>
              {`${res.text} (${formatInstant(res.releasedAt)})`}
            </p>
          ))}
        </>
      )}
      {summary.clinicianText === null ? null : (
        <>
          <h3 className={styles.printHeading}>{wordsHeading}</h3>
          <p className={styles.printText}>{summary.clinicianText}</p>
        </>
      )}
    </>
  );
}

/** The A4 referral letter, with the summary exactly as snapshotted at issue. */
export function ReferralSheet({
  print,
  sheetRef,
}: {
  print: ReferralPrintView;
  sheetRef?: Ref<HTMLElement>;
}) {
  const r = print.referral;
  const summary = r.summary;
  return (
    <PrintSheet
      paper="a4"
      label={T.referral}
      {...(sheetRef === undefined ? {} : { ref: sheetRef })}
    >
      <PrintHeader facility={print.facility.name} title={title(T.referral, print.copy)} />
      <PrintLines
        items={[
          { label: T.referralNo, value: r.referralNumber, identifier: true },
          { label: T.issued, value: formatInstant(r.issuedAt) },
          { label: T.to, value: r.toFacilityName },
          ...(r.toFacilityMflCode === null
            ? []
            : [{ label: T.mflCode, value: r.toFacilityMflCode, identifier: true }]),
          ...(r.toService === null ? [] : [{ label: T.service, value: r.toService }]),
          { label: T.urgency, value: REFERRAL_URGENCY_LABELS[r.urgency] },
          ...patientLines(print.patient, r.issuedAt),
          { label: T.allergies, value: summaryAllergies(summary) },
        ]}
      />
      <h3 className={styles.printHeading}>{T.reason}</h3>
      <p className={styles.printText}>{r.reason}</p>
      <SummaryPrint summary={summary} wordsHeading={T.summary} />
      <ClinicianFooter display={r.authorDisplay} licence={r.authorLicenceDisplay} />
    </PrintSheet>
  );
}

export function PrintLoadError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <Notice tone="error" action={<Button onPress={retry}>Try again</Button>}>
      {error instanceof ApiError ? error.message : 'The document could not load. Try again.'}
    </Notice>
  );
}

function usePrinted(
  printKey: QueryKey,
  listKeys: readonly QueryKey[],
  mark: () => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mark,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: printKey });
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** Loads the sick sheet, shows it and prints it; every print is counted and audited. */
export function SickSheetPrinter({ id, listKeys }: { id: string; listKeys: readonly QueryKey[] }) {
  const sheet = useRef<HTMLElement>(null);
  const query = useQuery(sickSheetPrintQuery(id));
  const printed = usePrinted(sickSheetPrintQuery(id).queryKey, listKeys, () =>
    markSickSheetPrinted(id),
  );
  if (query.isPending) return <LoadingBlock label="Loading the sick sheet" />;
  if (query.isError)
    return <PrintLoadError error={query.error} retry={() => void query.refetch()} />;
  return (
    <div className={styles.printer}>
      <div className={styles.preview}>
        <SickSheetSheet print={query.data} sheetRef={sheet} />
      </div>
      {printed.isError ? (
        <Notice tone="error">
          The sick sheet printed, but Wodi could not count the copy. Check the connection.
        </Notice>
      ) : null}
      <Button
        variant="primary"
        onPress={() => {
          if (sheet.current === null) return;
          printSheet(sheet.current);
          printed.mutate();
        }}
      >
        Print sick sheet
      </Button>
    </div>
  );
}

/** Loads the referral letter, shows it and prints it; every print is counted and audited. */
export function ReferralPrinter({ id, listKeys }: { id: string; listKeys: readonly QueryKey[] }) {
  const sheet = useRef<HTMLElement>(null);
  const query = useQuery(referralPrintQuery(id));
  const printed = usePrinted(referralPrintQuery(id).queryKey, listKeys, () =>
    markReferralPrinted(id),
  );
  if (query.isPending) return <LoadingBlock label="Loading the referral letter" />;
  if (query.isError)
    return <PrintLoadError error={query.error} retry={() => void query.refetch()} />;
  return (
    <div className={styles.printer}>
      <div className={styles.preview}>
        <ReferralSheet print={query.data} sheetRef={sheet} />
      </div>
      {printed.isError ? (
        <Notice tone="error">
          The letter printed, but Wodi could not count the copy. Check the connection.
        </Notice>
      ) : null}
      <Button
        variant="primary"
        onPress={() => {
          if (sheet.current === null) return;
          printSheet(sheet.current);
          printed.mutate();
        }}
      >
        Print referral letter
      </Button>
    </div>
  );
}
